import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import cn from 'classnames';
import { CellMeasurerCache } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { isEmpty } from 'lodash-es';

import BrIcons from '@components/BrIcons';
import BrPaginatorV2 from '@components/BrPaginatorV2';
import BrSearchBox from '@components/BrSearchBox';

import { CheckboxColumn } from './columns';
import { RowSelector, ColumnSelector, TableRenderer, Selectors, BRTABLE_CONSTANTS } from './components';
import UnifiedFilter from './components/UnifiedFilter';
import './brTableV2.scss';

const DEFAULT_PER_PAGE_COUNT = 10;

export default class BrTableV2 extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    data: PropTypes.array.isRequired,
    dataKey: PropTypes.string,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        // see BrColumn for full definition
        key: PropTypes.string.isRequired,
        column: PropTypes.func, // one of the column rendering function
        displayName: PropTypes.string, // header display
        type: PropTypes.oneOf(['string', 'number', 'checkboxEnum', 'date']), // TODO: handling for date sort/search?
        isSearchable: PropTypes.bool,
        isAdvanceSort: PropTypes.bool,
        isFilterable: PropTypes.bool,
        addToUnifiedFilter: PropTypes.bool,
        unifiedFilterConfig: PropTypes.shape({
          valueTitleArrayMap: PropTypes.arrayOf(
            // Map values to title, eg- Enabled value in DB is true/false but we want to show Enabled/Disabled
            PropTypes.shape({ value: PropTypes.any, title: PropTypes.string }),
          ),
          forceAdd: PropTypes.bool, // use force Add when you want to show a hidden column values in the filter
        }),
      }),
    ).isRequired,
    minWidth: PropTypes.number, // min width the table will shrink to
    isDynamicRowHeight: PropTypes.bool, // content gets wrapped & height of the row gets adjusted
    isLoading: PropTypes.bool,
    topRightElement: PropTypes.element,
    searchPlaceholder: PropTypes.string,
    onStateChange: PropTypes.func, // simple callback to let parent know state have changed
    // handles advance filter logic (rows:array, filterConfig:obj) => filteredRows:array
    onAdvanceFilter: PropTypes.func,

    // enable row select checkbox
    selectRowConfig: PropTypes.shape({
      batchSelectOptions: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.any.isRequired, // unique key
          title: PropTypes.string.isRequired, // option display text
          condition: PropTypes.func.isRequired, // data row will be passed as param  (row) => {}
        }),
      ),
      batchSelectActions: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.any.isRequired, // unique key
          title: PropTypes.string, // option display text
          value: PropTypes.any, // if options not provided, this will become a standalone button
          onClick: PropTypes.func, // (selectedRows:array, item:{key, value}) => {}
          options: PropTypes.arrayOf(
            PropTypes.shape({
              value: PropTypes.any.isRequired,
              title: PropTypes.string.isRequired,
              onClick: PropTypes.func, // this will be called instead of onBatchSelectActions if provided.
              // (selectedRows:array, item:{key, value}) => {}
            }),
          ),
        }),
      ),
      onClickAction: PropTypes.func, // (selectedRows:array, item:{key, value}) => {}
    }),

    // enable column selector to display only those selected
    flexibleColumnConfig: PropTypes.shape({
      tableId: PropTypes.string.isRequired, // this is used for storing column configuration in cookie
      defaultColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
      // object of optional columns that can be hidden
      columnOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    }),

    // enable callback to refresh the data on page/sort/filter/search actions
    // by using this, the data param should contain only those that should be displayed
    ajaxConfig: PropTypes.shape({
      // ({page, numPerPage, query, sortConfig: {columnKey:string,orderBy:string}, filterConfig:{}}) =>
      onFetch: PropTypes.func.isRequired,
      totalDataLength: PropTypes.number.isRequired, // used for determining paginations
      defaultSortColumnKey: PropTypes.string,
      defaultSortOrderBy: PropTypes.oneOf([BRTABLE_CONSTANTS.ASC, BRTABLE_CONSTANTS.DESC, BRTABLE_CONSTANTS.NONE]),
    }),
    customSearchComponent: PropTypes.element,
    dataTimeFilterConfig: PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
    }), // Provide the key for which u want the date filter to work on
    scrollElement: PropTypes.object,
    highlightedRowKeys: PropTypes.array,
    onRowClick: PropTypes.func, // onRowClick({event, index, rowData})
    isPaginated: PropTypes.bool,
    showAppliedFilters: PropTypes.bool,
    minRowHeight: PropTypes.number,
    reloadOnColumnChange: PropTypes.bool,
    computedWidth: PropTypes.number,
    computedHeight: PropTypes.number,
    useDefaultRowConfigValues: PropTypes.bool,
    useBulkActions: PropTypes.bool,
    selectedRows: PropTypes.array,
  };

  static defaultProps = {
    isLoading: false,
    isDynamicRowHeight: false,
    minWidth: 600,
    highlightedRowKeys: [],
    isPaginated: true,
    showAppliedFilters: false,
    reloadOnColumnChange: false,
    computedWidth: 0,
    computedHeight: 0,
    useDefaultRowConfigValues: true,
    useBulkActions: false,
    selectedRows: [],
  };

  constructor(props) {
    super(props);
    this.state = this.getCleanTableState(props); // may use initial props
  }

  _cache = new CellMeasurerCache({
    minHeight: this.props.minRowHeight || 72,
    fixedWidth: true,
  });

  getCleanTableState = (props) => {
    const { ajaxConfig, selectRowConfig, flexibleColumnConfig, columns, isPaginated, selectedRows } = props;

    const isAjax = !!ajaxConfig;
    const isRowSelectable = !!selectRowConfig;
    const isColFlexible = !!flexibleColumnConfig;

    const allColumns = [...columns];
    const displayColumns = !isColFlexible
      ? columns.filter((col) => !!col.column).map((col) => col.key)
      : CookieUtils.getCookie(flexibleColumnConfig.tableId) || [...flexibleColumnConfig.defaultColumns];
    const defaultColumns = !isColFlexible ? [] : [...flexibleColumnConfig.defaultColumns];

    // add row selector checkbox column
    if (isRowSelectable) {
      const key = '__checkbox__';
      allColumns.unshift({
        key,
        displayName: ' ',
        column: CheckboxColumn,
        columnConfig: { onClick: this.handleCheckRow },
      });
      defaultColumns.unshift(key);
      if (!displayColumns.includes(key)) {
        displayColumns.unshift(key);
      }
    }
    const newState = {
      page: 1,
      numPerPage: isPaginated ? DEFAULT_PER_PAGE_COUNT : Number.MAX_SAFE_INTEGER,
      selectedRows: selectedRows ?? [],
      query: props.query || '',
      allColumns,
      defaultColumns,
      displayColumns,
      isAjax,
      isRowSelectable,
      isColFlexible,
      columnMap: allColumns.reduce((pv, col) => {
        pv[col.key] = col;
        return pv;
      }, {}),
      filterConfig: {},
      sortConfig: {
        columnKey: (isAjax && ajaxConfig.defaultSortColumnKey) || '',
        orderBy: (isAjax && ajaxConfig.defaultSortOrderBy) || '',
      },
      unifiedFilterState: { allFilters: [] },
      expandedRowIndex: -1,
    };
    return newState;
  };

  updateExpandedRowIndex = (rowIndex) => {
    this._cache.clearAll();
    this.tableDom.recomputeRowHeights(rowIndex);
    this.setState({ expandedRowIndex: rowIndex });
  };

  componentDidMount() {
    const { onStateChange, columns, data } = this.props;
    this.setUnifiedFilterState(columns, data);

    if (onStateChange) {
      onStateChange(this.state);
    }
  }

  setUnifiedFilterState = (allColumns, data) => {
    const { displayColumns } = this.state;
    const columnsInUnifiedFilter = this.getColumnsInUnifiedFilter(allColumns, displayColumns);
    const _unifiedFilterState = { allFilters: [] };
    if (!isEmpty(columnsInUnifiedFilter)) {
      _unifiedFilterState.allFilters = this.getAllFilters(columnsInUnifiedFilter, data);
    }
    this.setState({ unifiedFilterState: _unifiedFilterState });
  };

  getAllFilters(columnsInUnifiedFilter, data) {
    return columnsInUnifiedFilter.map((column) => {
      const filterData = {};
      filterData.displayName = column.displayName;
      filterData.key = column.key;
      filterData.filters = this.getFilters(data, column);
      return filterData;
    });
  }

  getFilters(data, column) {
    const filters = [];
    const uniqueValuesOfColumn = new Set();
    const { key } = column;
    const { unifiedFilterConfig } = column;
    data.forEach((entry) => uniqueValuesOfColumn.add(entry[key].value));
    // eslint-disable-next-line max-len, operator-linebreak
    const valueTitleArrayMap =
      // eslint-disable-next-line max-len
      unifiedFilterConfig && unifiedFilterConfig.valueTitleArrayMap ? unifiedFilterConfig.valueTitleArrayMap : []; // i hate myself for this... unifiedFilterConfig?.valueTitleArrayMap || [] not working babel version issue ?

    for (const uniqueValue of uniqueValuesOfColumn.keys()) {
      const valueTitleMap = valueTitleArrayMap.find((valueTitleMap) => valueTitleMap.value === uniqueValue);
      const title = valueTitleMap ? valueTitleMap.title : uniqueValue;
      filters.push({ value: uniqueValue, title, selected: false });
    }
    return filters;
  }

  getColumnsInUnifiedFilter = (allColumns, displayColumns) => {
    return allColumns.filter(
      (col) => (col.unifiedFilterConfig && col.unifiedFilterConfig.forceAdd)
        || (displayColumns.includes(col.key) && col.addToUnifiedFilter),
    );
  };

  resetSelectedRows = (selectedRows) => {
    this.setState({ selectedRows: selectedRows ?? [] });
  };

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    // setState -> no rerender
    // this step is will not be invoked by this.setState
    if (nextProps.data !== this.props.data) {
      this.resetSelectedRows(nextProps.selectedRows);
      this.setUnifiedFilterState(nextProps.columns, nextProps.data);
      this.forceUpdateTable();
    }

    if (nextProps?.selectedRows !== this.props?.selectedRows) {
      this.resetSelectedRows(nextProps.selectedRows);
    }

    if (nextProps.query !== this.props.query) {
      this.setState({ query: nextProps.query });
    }

    // in case where table is swapped by prop update, we need to reset columns and other configs
    const { flexibleColumnConfig } = nextProps;
    if (
      (flexibleColumnConfig && flexibleColumnConfig.tableId !== this.props.flexibleColumnConfig.tableId)
      || (this.props.reloadOnColumnChange && nextProps.columns !== this.props.columns)
    ) {
      this.setState(this.getCleanTableState(nextProps));
      this.forceUpdateTable();
    }
  }

  handleTableLoaded = (tableDom) => {
    this.tableDom = tableDom;
  };

  forceUpdateTable = () => {
    if (this.props.isDynamicRowHeight) {
      this._cache.clearAll();
      if (this.tableDom) {
        this.tableDom.recomputeRowHeights();
      }
    }

    if (this.tableDom) {
      this.tableDom.recomputeGridSize();
    }
  };

  handleQueryChange = (query) => this.handleViewChanges({ type: 'queryChange', query });
  handleQuerySubmit = (query) => this.handleViewChanges({ type: 'querySubmit', query });
  handleChangePage = (page) => this.handleViewChanges({ type: 'page', page });
  handleChangeNumPerPage = (numPerPage) => this.handleViewChanges({ type: 'numPerPage', numPerPage });

  handleSortTable = (newColumnKey, newOrderBy) => {
    const {
      sortConfig: { columnKey, orderBy },
    } = this.state;
    const nextSortConfig = {
      columnKey: newColumnKey,
      orderBy: newOrderBy || BRTABLE_CONSTANTS.ASC,
    };
    // if newOrderBy not specified, simply rotate through from ASC, DESC, to none
    if (columnKey === newColumnKey && !newOrderBy) {
      if (orderBy === BRTABLE_CONSTANTS.ASC) {
        nextSortConfig.orderBy = BRTABLE_CONSTANTS.DESC;
      } else {
        nextSortConfig.column = '';
      }
    }
    this.handleViewChanges({ type: 'sortConfig', sortConfig: nextSortConfig });
  };

  handleSetFilter = (columnKey, filter) => {
    const newConfig = { ...this.state.filterConfig };
    if (!filter) {
      delete newConfig[columnKey];
    } else {
      newConfig[columnKey] = filter;
    }
    this.handleViewChanges({ type: 'filterConfig', filterConfig: newConfig });
  };

  applyUnifiedFilter = (newState) => {
    this.handleViewChanges({
      type: 'unifiedFilterState',
      unifiedFilterState: newState,
    });
  };

  handleColumnUpdate = (displayColumns) => {
    // generate new displayColumns based on the order of state.allColumns
    const newDisplayColumns = this.state.allColumns
      .filter((col) => displayColumns.includes(col.key))
      .map((col) => col.key);
    CookieUtils.setCookie(this.props.flexibleColumnConfig.tableId, newDisplayColumns);
    this.handleViewChanges({
      type: 'displayColumns',
      displayColumns: newDisplayColumns,
    });
  };

  handleCheckRow = (row, isCheck) => {
    const { selectedRows } = this.state;
    if (isCheck) {
      this.setState({ selectedRows: [...selectedRows, row] });
    } else {
      // eslint-disable-next-line eqeqeq
      this.setState({ selectedRows: selectedRows.filter((r) => r != row) });
    }
    this.forceUpdateTable();
  };

  applyBulkActions = (dataset, selectedRows, dataKey, condition, action) => {
    if (action === 'All') {
      const allDataset = Selectors.sortedDataSelector({ ...this.props, ...this.state });
      const rowsSelected = allDataset.filter((item) => condition(item));
      this.setState({ selectedRows: rowsSelected });
    } else if (action === 'Partial') {
      this.applyPartialBulkAction(dataset, selectedRows, dataKey, condition);
    } else {
      this.setState({ selectedRows: [] });
    }
  };

  applyPartialBulkAction = (dataset, selectedRows, dataKey, condition) => {
    const alreadySelectedIds = selectedRows.map((row) => row[dataKey].value);
    const toAdd = dataset.filter((item) => condition(item) && !alreadySelectedIds.includes(item[dataKey].value));
    const toRemove = dataset.filter((item) => !condition(item)).map((item) => item[dataKey].value);
    let _selRows = selectedRows.filter((item) => !toRemove.includes(item[dataKey].value));
    _selRows = _selRows.concat(toAdd);
    this.setState({ selectedRows: _selRows });
  };

  handleBatchSelect = (condition, action) => {
    const { selectedRows = [] } = this.state;
    const { dataKey, useBulkActions } = this.props;
    const dataset = this.state.isAjax
      ? Selectors.hideAnimationDataSelector({ ...this.props, ...this.state })
      : Selectors.displayDataSelector({
        ...this.props,
        ...this.state,
        filterHandler: this.props.onAdvanceFilter,
      });
    if (useBulkActions) {
      this.applyBulkActions(dataset, selectedRows, dataKey, condition, action);
    } else {
      this.applyPartialBulkAction(dataset, selectedRows, dataKey, condition);
    }
    this.forceUpdateTable();
  };

  handleViewChanges = ({
    type,
    query,
    page,
    numPerPage,
    sortConfig,
    filterConfig,
    displayColumns,
    unifiedFilterState,
    customSearchLogic,
  }) => {
    const { ajaxConfig, onStateChange } = this.props;
    const {
      isAjax,
      page: prevPage,
      selectedRows: prevSelectedRows,
      query: prevQuery,
      numPerPage: prevNumPerPage,
      sortConfig: prevSortConfig,
      filterConfig: prevFilterConfig,
      displayColumns: prevDisplayColumns,
      unifiedFilterState: prevUnifiedFilterState,
      customSearchLogic: prevCustomSearchLogic,
    } = this.state;
    // compute new states
    const newState = {
      page: page || 1,
      selectedRows: prevSelectedRows || [],
      query: query !== undefined ? query : prevQuery, // since query can be empty string, compare with undefined
      numPerPage: numPerPage || prevNumPerPage,
      sortConfig: sortConfig || prevSortConfig,
      filterConfig: filterConfig || prevFilterConfig,
      displayColumns: displayColumns || prevDisplayColumns,
      unifiedFilterState: unifiedFilterState || prevUnifiedFilterState,
      customSearchLogic: customSearchLogic || prevCustomSearchLogic,
    };

    if (isAjax) {
      if (['queryChange', 'displayColumns'].includes(type)) {
        // for ajax call, queryChange/displayColumns shouldn't trigger table change/ajax call
        newState.page = prevPage;
      } else if (ajaxConfig.onFetch) {
        ajaxConfig.onFetch(newState);
      }
    }

    this.setState(newState);
    this.forceUpdateTable();

    if (onStateChange) {
      onStateChange(newState);
    }
  };

  setCustomSearchLogic = (logic) => {
    this.handleViewChanges({
      type: 'customSearchLogic',
      customSearchLogic: logic,
    });
  };

  getSearchBox = (selectRowConfig, selectedRows, displayData) => {
    const { customSearchComponent, searchPlaceholder, isAjax, useDefaultRowConfigValues, useBulkActions } = this.props;
    let dataset = [];
    if (useBulkActions) {
      dataset = Selectors.sortedDataSelector({ ...this.props, ...this.state });
      dataset = this.props.data?.filter((row) => {
        const { isEditable, isLive } = row.Original_Data;
        const isReadOnly = !isEditable || isLive;
        return !isReadOnly;
      });
    }
    const { query } = this.state;
    return customSearchComponent ? (
      React.cloneElement(customSearchComponent, {
        setCustomSearchLogic: this.setCustomSearchLogic,
      })
    ) : (
      <div className="search-container">
        {useBulkActions && (
          <>
            <RowSelector
              selectedRows={selectedRows}
              displayData={displayData}
              onHandleBatchSelect={this.handleBatchSelect}
              selectRowConfig={selectRowConfig}
              useDefaultRowConfigValues={useDefaultRowConfigValues}
              totalDataLength={dataset?.length || 0}
              useBulkActions={useBulkActions}
            />
            <div className="seperator" />
          </>
        )}
        <BrSearchBox
          query={query}
          placeholder={searchPlaceholder}
          onQuerySubmit={this.handleQuerySubmit}
          onQueryChange={this.handleQueryChange}
          isAjaxSearch={isAjax}
        />
      </div>
    );
  };

  removeFilter = (removedFilter) => {
    const { unifiedFilterState } = this.state;
    const newFilterState = {};
    newFilterState.allFilters = [];
    unifiedFilterState.allFilters.forEach((filterByKey) => {
      const newFilterByKey = { ...filterByKey };
      newFilterByKey.filters = [...filterByKey.filters];
      if (newFilterByKey.key === removedFilter.key) {
        // eslint-disable-next-line eqeqeq
        const filterToRemove = newFilterByKey.filters.find((entry) => entry.value == removedFilter.value);
        filterToRemove.selected = false;
      }
      newFilterState.allFilters.push(newFilterByKey);
    });
    this.applyUnifiedFilter(newFilterState);
  };

  getFilterBreadcrums = (appliedFilter) => {
    return (
      <span className="applied-filter-breadcrums" key={appliedFilter.displayName + appliedFilter.title}>
        <span>{appliedFilter.displayName}</span>
        <span> : </span>
        <span>{appliedFilter.title}</span>
        <BrIcons.Cross onClick={() => this.removeFilter(appliedFilter)} />
      </span>
    );
  };

  getMoreFiltersLink = (appliedFilters) => {
    return (
      <div className="more-filters-wrapper-container">
        <span className="more-filters-link">
          {' '}
          +
          {appliedFilters.length}
          {' '}
          More
          {' '}
        </span>
        <div className="more-filters-dropdown-container">
          {appliedFilters.map((appliedFilter) => this.getFilterBreadcrums(appliedFilter))}
        </div>
      </div>
    );
  };

  getAppliedFilters = () => {
    const { showAppliedFilters } = this.props;
    const { unifiedFilterState } = this.state;
    const selectedFilters = [];
    if (!showAppliedFilters) {
      return null;
    }
    unifiedFilterState.allFilters.forEach((filterObj) => {
      filterObj.filters.forEach((filter) => {
        if (filter.selected) {
          selectedFilters.push({ ...filter, displayName: filterObj.displayName, key: filterObj.key });
        }
      });
    });
    if (selectedFilters.length === 0) {
      return null;
    }
    return (
      <div className="applied-filter-container">
        <span>Filtered by : </span>
        {selectedFilters.splice(0, 3).map((appliedFilter) => this.getFilterBreadcrums(appliedFilter))}
        {selectedFilters.length > 0 ? this.getMoreFiltersLink(selectedFilters) : null}
      </div>
    );
  };

  render() {
    const {
      className,
      data,
      minWidth,
      topRightElement,
      topLeftElement,
      ajaxConfig,
      selectRowConfig,
      flexibleColumnConfig,
      isLoading,
      onAdvanceFilter,
      scrollElement,
      dataKey,
      highlightedRowKeys,
      onRowClick,
      isDynamicRowHeight,
      isPaginated,
      computedWidth,
      computedHeight,
      useBulkActions,
    } = this.props;
    const {
      columnMap,
      defaultColumns,
      displayColumns,
      page,
      numPerPage,
      query,
      isAjax,
      isRowSelectable,
      selectedRows,
      sortConfig,
      filterConfig,
      unifiedFilterState,
      customSearchLogic,
      expandedRowIndex,
    } = this.state;

    const classname = cn('br-table-v2', className);
    const selectorProps = {
      data,
      page,
      numPerPage,
      query,
      columnMap,
      sortConfig,
      filterConfig,
      unifiedFilterState,
      filterHandler: onAdvanceFilter,
      customSearchLogic,
    };
    const [totalDataLength, displayData] = isAjax
      ? [ajaxConfig.totalDataLength, Selectors.hideAnimationDataSelector({ data, displayColumns })]
      : [Selectors.advancedFilteredDataSelector(selectorProps).length, Selectors.displayDataSelector(selectorProps)];

    return (
      <div className={classname}>
        <div className="flexrow">
          {topLeftElement}
          {this.getSearchBox(selectRowConfig, selectedRows, displayData, totalDataLength)}
          {this.getAppliedFilters()}
          <div className="flexdiv" />
          {unifiedFilterState.allFilters.length > 0 ? (
            <div className="unified-filter-container">
              <UnifiedFilter
                unifiedFilterState={unifiedFilterState}
                applyUnifiedFilter={this.applyUnifiedFilter}
                isDataLoading={isLoading}
              />
            </div>
          ) : null}
          {topRightElement}
        </div>
        {selectRowConfig && selectedRows.length > 0 && !useBulkActions ? (
          <div className={(selectRowConfig || flexibleColumnConfig) && 'flexrow'}>
            <RowSelector
              selectedRows={selectedRows}
              displayData={displayData}
              onHandleBatchSelect={this.handleBatchSelect}
              selectRowConfig={selectRowConfig}
            />
          </div>
        ) : null}
        <div className="flexdiv" />
        {flexibleColumnConfig && (
          <ColumnSelector
            columnMap={columnMap}
            displayColumns={displayColumns}
            defaultColumns={defaultColumns}
            columnOptions={flexibleColumnConfig.columnOptions}
            onUpdateDisplayColumns={this.handleColumnUpdate}
          />
        )}

        <TableRenderer
          expandedRowIndex={expandedRowIndex}
          _cache={this._cache}
          updateExpandedRowIndex={this.updateExpandedRowIndex}
          columnMap={columnMap}
          displayColumns={displayColumns}
          displayData={displayData}
          minWidth={minWidth}
          isRowSelectable={isRowSelectable}
          sortConfig={sortConfig}
          filterConfig={filterConfig}
          selectedRows={selectedRows}
          isLoading={isLoading}
          onTableLoaded={this.handleTableLoaded}
          onSortClicked={this.handleSortTable}
          onSetFilter={this.handleSetFilter}
          scrollElement={scrollElement}
          dataKey={dataKey}
          highlightedRowKeys={highlightedRowKeys}
          onRowClick={onRowClick}
          isDynamicRowHeight={isDynamicRowHeight}
          computedWidth={computedWidth}
          computedHeight={computedHeight}
          useBulkActions={useBulkActions}
        />
        {isPaginated && totalDataLength > 0 && (
          <BrPaginatorV2
            currentPage={page}
            numPerPage={numPerPage}
            totalCount={totalDataLength}
            onChangePage={this.handleChangePage}
            onChangeNumPerPage={this.handleChangeNumPerPage}
          />
        )}
      </div>
    );
  }
}
